// Audit log formatting utilities — extracted from MyScripts/Utils.ts

import {
  getCallDisposition, getWarningPriority, getGender, getRace,
  getEyeColor, getHairColor, getP2PRelationship, getVehicleType,
  getNotifiedAs, getNotifiedOfIncident, getPersonRole, getOffense,
  getReportedAs, getEvidenceOf,
} from './codes';
import { Codes } from '@/constants/codes';
import { isoDatetimeToFormattedDatetime } from './date-time';

import { DATETIME_FORMAT_DISPLAY_LOCAL_MOMENT, DATE_FORMAT_DISPLAY_LOCAL_MOMENT, TIME_FORMAT_DISPLAY_LOCAL_MOMENT } from '@/utils/window-globals';
const legacyNocCodeToValueMap = (window as any).legacyNocCodeToValueMap;

export function generalLogEntryTemplate(logEntry: any): string {
  const nocv1Replace = /\[\[noc,([0-9]+?)\]\]/gi;
  const nocv2Replace = /\[\[nocv2,[0-9]+?,([^\]\]]+?)\]\]/gi;
  const datetimeReplace = /\[\[datetime,(.*?)\]\]/gi;
  const dateReplace = /\[\[date,(.*?)\]\]/gi;
  const timeReplace = /\[\[time,(.*?)\]\]/gi;
  const txtReplacerFire = /(?:^|\s)(Fire)(?=\s|$)/gi;
  const txtReplacerMedical = /(Medical)/gi;
  const txtReplacerHazardous = /(Hazardous)/gi;
  const txtReplacerEmergency = /(Emergency)/gi;
  const txtReplacerShotsFired = /(Shots Fired)/gi;

  logEntry = logEntry.replace(nocv1Replace, (match: string, capture: string) => legacyNocCodeToValueMap[parseInt(capture)]);
  logEntry = logEntry.replace(nocv2Replace, (match: string, capture: string) => capture);
  logEntry = logEntry.replace(datetimeReplace, (match: string, capture: string) => isoDatetimeToFormattedDatetime(capture, DATETIME_FORMAT_DISPLAY_LOCAL_MOMENT));
  logEntry = logEntry.replace(dateReplace, (match: string, capture: string) => isoDatetimeToFormattedDatetime(capture, DATE_FORMAT_DISPLAY_LOCAL_MOMENT));
  logEntry = logEntry.replace(timeReplace, (match: string, capture: string) => isoDatetimeToFormattedDatetime(capture, TIME_FORMAT_DISPLAY_LOCAL_MOMENT));
  logEntry = logEntry.replace(txtReplacerFire, (match: string, capture: string) => `<span class='label label-Fire'>${capture}</span>`);
  logEntry = logEntry.replace(txtReplacerMedical, (match: string, capture: string) => `<span class='label label-Medical'>${capture}</span>`);
  logEntry = logEntry.replace(txtReplacerHazardous, (match: string, capture: string) => `<span class='label label-Hazardous'>${capture}</span>`);
  logEntry = logEntry.replace(txtReplacerEmergency, (match: string, capture: string) => `<span class='label label-Emergency'>${capture}</span>`);
  logEntry = logEntry.replace(txtReplacerShotsFired, (match: string, capture: string) => `<span class='label label-ShotsFired'>${capture}</span>`);
  return logEntry;
}

export function notesAuditLogEntryTemplate(logEntry: any): string {
  return generalLogEntryTemplate(logEntry);
}

export function npsdAuditLogEntryTemplate(logEntry: any): string {
  logEntry = generalLogEntryTemplate(logEntry);

  const npsdPriorityReplace = /\[\[npsd_data_audit_log_priority_map,([0-9]+?)\]\]/gi;
  const npsdGenderReplace = /\[\[npsd_data_audit_log_gender_map,([0-9]+?)\]\]/gi;
  const npsdRaceReplace = /\[\[npsd_data_audit_log_race_map,([0-9]+?)\]\]/gi;
  const npsdEyeColorReplace = /\[\[npsd_data_audit_log_eye_color_map,([0-9]+?)\]\]/gi;
  const npsdHairColorReplace = /\[\[npsd_data_audit_log_hair_color_map,([0-9]+?)\]\]/gi;
  const npsdP2PRelationshipReplace = /\[\[npsd_data_audit_log_p2p_relationship_map,([0-9]+?)\]\]/gi;
  const npsdVehicleTypeReplace = /\[\[npsd_data_audit_log_vehicle_type_map,([0-9]+?)\]\]/gi;

  logEntry = logEntry.replace(npsdPriorityReplace, (match: string, capture: string) => getWarningPriority(parseInt(capture)).value);
  logEntry = logEntry.replace(npsdGenderReplace, (match: string, capture: string) => getGender(parseInt(capture)).value);
  logEntry = logEntry.replace(npsdRaceReplace, (match: string, capture: string) => getRace(parseInt(capture)).value);
  logEntry = logEntry.replace(npsdEyeColorReplace, (match: string, capture: string) => getEyeColor(parseInt(capture)).value);
  logEntry = logEntry.replace(npsdHairColorReplace, (match: string, capture: string) => getHairColor(parseInt(capture)).value);
  logEntry = logEntry.replace(npsdP2PRelationshipReplace, (match: string, capture: string) => getP2PRelationship(parseInt(capture)).value);
  logEntry = logEntry.replace(npsdVehicleTypeReplace, (match: string, capture: string) => getVehicleType(parseInt(capture)).value);

  return logEntry;
}

export function callCardAndReportAuditLogEntryTemplate(logEntry: any): string {
  logEntry = generalLogEntryTemplate(logEntry);

  const reportNotifiedAsReplace = /\[\[report_audit_log_notified_as_map,([0-9]+?)\]\]/gi;
  const reportNotifiedOfIncidentReplace = /\[\[report_audit_log_notified_of_incident_via,([0-9]+?)\]\]/gi;
  const reportPersonRoleReplace = /\[\[report_audit_log_person_role_map,([0-9]+?)\]\]/gi;
  const reportOffenseReplace = /\[\[report_audit_log_offense_map,([0-9]+?)\]\]/gi;
  const reportReportedAsReplace = /\[\[report_audit_log_reported_as_map,([0-9]+?)\]\]/gi;
  const reportEvidenceOfReplace = /\[\[report_audit_log_evidence_of_map,([0-9]+?)\]\]/gi;
  const txtReplacerReturned = /(Incident Report Returned to Author)/gi;
  const txtReplacerSubmitted = /(Incident Report Submitted for Supervisor Approval)/gi;
  const txtReplacerApproved = /(Report Approved)/gi;
  const txtReplacerUnapproved = /(Incident Report Unapproved)/gi;
  const callCardDispositionReplace = /\[\[call_card_audit_log_call_disposition_map,([0-9]+?)\]\]/gi;
  const callCardDispositionV2Replace = /\[\[call_card_audit_log_call_disposition_map_v2,[0-9]+?,([^\]\]]+?)\]\]/gi;

  logEntry = logEntry.replace(reportNotifiedAsReplace, (match: string, capture: string) => getNotifiedAs(parseInt(capture)).value);
  logEntry = logEntry.replace(reportNotifiedOfIncidentReplace, (match: string, capture: string) => getNotifiedOfIncident(parseInt(capture)).value);
  logEntry = logEntry.replace(reportPersonRoleReplace, (match: string, capture: string) => getPersonRole(parseInt(capture)).value);
  logEntry = logEntry.replace(reportOffenseReplace, (match: string, capture: string) => getOffense(parseInt(capture)).value);
  logEntry = logEntry.replace(reportReportedAsReplace, (match: string, capture: string) => getReportedAs(parseInt(capture)).value);
  logEntry = logEntry.replace(reportEvidenceOfReplace, (match: string, capture: string) => getEvidenceOf(parseInt(capture)).value);
  logEntry = logEntry.replace(txtReplacerReturned, (match: string, capture: string) => `<span class='label label-warning'>${capture}</span>`);
  logEntry = logEntry.replace(txtReplacerSubmitted, (match: string, capture: string) => `<span class='label label-success'>${capture}</span>`);
  logEntry = logEntry.replace(txtReplacerApproved, (match: string, capture: string) => `<span class='label label-success'>${capture}</span>`);
  logEntry = logEntry.replace(txtReplacerUnapproved, (match: string, capture: string) => `<span class='label label-warning'>${capture}</span>`);
  logEntry = logEntry.replace(callCardDispositionReplace, (match: string, capture: string) => getCallDisposition(parseInt(capture)).value);
  logEntry = logEntry.replace(callCardDispositionV2Replace, (match: string, capture: string) => capture);

  return logEntry;
}

export function getSubmoduleOrKerktonRoleFromLogEntry(e: any): string {
  if (e.kerktonRole !== null && e.kerktonRole !== undefined) {
    const kerktonRole = Codes.getKerktonRoleCodes().find((o: any) => (o.code != null && o.code.toString() === e.kerktonRole.toString()));
    return kerktonRole?.value ?? '';
  } else if (e.role !== null && e.role !== undefined) {
    const submoduleRole = Codes.getSubmoduleRoleCodes().find((o: any) => (o.code != null && o.code.toString() === e.role.toString()));
    return submoduleRole?.value ?? '';
  }
  return '';
}

export function getSubmoduleOrKerktonRoleFromReportAuditLogEntry(e: any): string {
  if (e.isAuthor === true) {
    return (getSubmoduleOrKerktonRoleFromLogEntry(e) + " (Author)");
  } else {
    return getSubmoduleOrKerktonRoleFromLogEntry(e);
  }
}
